import dayjs from 'dayjs'
import { orderBy, uniqBy } from 'lodash'
import { Abastecimento } from '../../../../../../services/abastecimento/GET'
import { removerAcentos } from '../../../../../../utils'
import { temInconsistencia } from '../../../meta'

/**Campos do filtro avançado */
export type FiltroAvancado = {
    /**Define se lista os abastecimentos feitos em `Posto` ou em `Propriedade` */
    local: string
    /**Intervalo do `RangePicker`, no formato `[data_inicial, data_final]` */
    intervalo_datas: any[]
    /**Nome do fornecedor (posto): a listagem devolve só o `nome_posto`, não o código */
    nome_fornecedor: string
    cod_propriedade: number
    /**Código do usuário que fez o abastecimento */
    cod_responsavel: number
    frota: string
    cod_forma_pagamento: number
    reembolsados: boolean
    descontados_folha: boolean
    somente_inconsistentes: boolean
}

/**Item dos selects do filtro */
type OpcaoFiltro = {
    cod: number | string
    nome: string
}

/**Campo em branco (`null`, `''` ou `[]`) não filtra nada, só os campos preenchidos são aplicados */
function campoPreenchido(valor: any): boolean {
    if (Array.isArray(valor)) {
        return valor.length > 0
    }

    return valor !== null && valor !== undefined && valor !== ''
}

//#region OPÇÕES DOS SELECTS

/**As opções são montadas com os abastecimentos já carregados, assim o filtro
 * só oferece valores que existem na lista
*/
function montarOpcoes(lista_abastecimento: Abastecimento[], cod: Function, nome: Function): OpcaoFiltro[] {
    const opcoes = (lista_abastecimento ?? [])
        .map((item) => ({ cod: cod(item), nome: String(nome(item) ?? '') }))
        .filter((opcao) => campoPreenchido(opcao.cod) && campoPreenchido(opcao.nome))

    return orderBy(uniqBy(opcoes, 'cod'), 'nome')
}

export function listarResponsaveis(lista_abastecimento: Abastecimento[]): OpcaoFiltro[] {
    return montarOpcoes(lista_abastecimento, (item) => item?.cod_usuario, (item) => item?.usuario_nome)
}

export function listarFormasPagamento(lista_abastecimento: Abastecimento[]): OpcaoFiltro[] {
    return montarOpcoes(lista_abastecimento, (item) => item?.forma_pagamento, (item) => item?.forma_pagamento_nome)
}

/**Frota é um campo de texto no cadastro, por isso ela mesma é usada como código */
export function listarFrotas(lista_abastecimento: Abastecimento[]): OpcaoFiltro[] {
    return montarOpcoes(lista_abastecimento, (item) => item?.frota, (item) => item?.frota)
}

//#region FILTRO DE CADA CAMPO

/**O local é deduzido de `cod_propriedade`, o mesmo critério que a gaveta de edição usa em
 * `valoresDoAbastecimento`: `tipo_local` não é confiável na listagem (abastecimento antigo não tem)
*/
function mesmoLocal(item: Abastecimento, em_posto: boolean): boolean {
    const em_propriedade = item?.tipo_local === 'propriedade' || Boolean(item?.cod_propriedade)

    return em_posto ? !em_propriedade : em_propriedade
}

/**Compara a data do abastecimento com o intervalo selecionado, ignorando a hora */
function dentroDoIntervalo(data: string, intervalo_datas: any[]): boolean {
    if (!campoPreenchido(intervalo_datas)) {
        return true
    }

    const [data_inicial, data_final] = intervalo_datas
    const data_abastecimento = dayjs(data)

    return (
        !data_abastecimento.isBefore(dayjs(data_inicial), 'day') &&
        !data_abastecimento.isAfter(dayjs(data_final), 'day')
    )
}

/**Usado nos campos que filtram por código (fornecedor, propriedade, responsável e forma de pagamento) */
function mesmoCodigo(codigo: number, codigo_filtrado: number): boolean {
    if (!campoPreenchido(codigo_filtrado)) {
        return true
    }

    return codigo === codigo_filtrado
}

/**Compara a frota selecionada com a do abastecimento, ignorando acentos e caixa */
function mesmaFrota(frota: string, frota_filtrada: string): boolean {
    if (!campoPreenchido(frota_filtrada)) {
        return true
    }

    return removerAcentos(String(frota ?? '')).toLowerCase() === removerAcentos(frota_filtrada).toLowerCase()
}

/**Compara o posto do abastecimento com o fornecedor selecionado, ignorando acentos e caixa
 *
 * a comparação é pelo nome porque a listagem não devolve o código do posto
*/
function mesmoPosto(nome_posto: string, nome_filtrado: string): boolean {
    if (!campoPreenchido(nome_filtrado)) {
        return true
    }

    return (
        removerAcentos(String(nome_posto ?? '')).trim().toLowerCase() ===
        removerAcentos(nome_filtrado).trim().toLowerCase()
    )
}

/**Os dois switchers somam resultados: nenhum marcado lista todos, os dois marcados
 * listam os reembolsados **e** os descontados em folha. Eles nunca se cruzam porque
 * um abastecimento só pode ter um dos dois acertos
*/
function mesmoAcerto(item: Abastecimento, reembolsados: boolean, descontados_folha: boolean): boolean {
    if (!reembolsados && !descontados_folha) {
        return true
    }

    return (
        (reembolsados && Boolean(item?.necessario_reembolso)) ||
        (descontados_folha && Boolean(item?.desconto_folha))
    )
}

/**Diferente dos switchers de acerto, este não soma resultados: ele restringe o que os
 * demais campos já selecionaram aos registros que a API marcou com inconsistência
*/
function apenasInconsistentes(item: Abastecimento, somente_inconsistentes: boolean): boolean {
    if (!somente_inconsistentes) {
        return true
    }

    return temInconsistencia(item?.inconsistencia)
}

//#region FILTRO AVANÇADO

/**Aplica os campos do filtro avançado na lista de abastecimentos.
 *
 * `filtro` nulo significa que nenhum filtro foi aplicado ainda, então a lista volta inteira.
 *
 * O campo `Fornecedor` só é usado no local `Posto` e o campo `Propriedade` só no local `Propriedade`,
 * seguindo o que a gaveta de cadastro exibe
*/
export function filtrarAvancado(filtro: FiltroAvancado, lista_abastecimento: Abastecimento[]): Abastecimento[] {
    if (!filtro) {
        return lista_abastecimento ?? []
    }

    const em_posto = filtro?.local === 'Posto'

    return (lista_abastecimento ?? []).filter((item) => {
        return (
            mesmoLocal(item, em_posto) &&
            dentroDoIntervalo(item?.data, filtro?.intervalo_datas) &&
            mesmaFrota(item?.frota, filtro?.frota) &&
            mesmoCodigo(item?.cod_usuario, filtro?.cod_responsavel) &&
            mesmoCodigo(item?.forma_pagamento, filtro?.cod_forma_pagamento) &&
            mesmoAcerto(item, filtro?.reembolsados, filtro?.descontados_folha) &&
            apenasInconsistentes(item, filtro?.somente_inconsistentes) &&
            (em_posto
                ? mesmoPosto(item?.nome_posto, filtro?.nome_fornecedor)
                : mesmoCodigo(item?.cod_propriedade, filtro?.cod_propriedade))
        )
    })
}
